'use client';

import useSWR from 'swr';
import { plannerCounselSlotApi } from '@/entities/planner/model/planner-counsel-slot.api';
import { searchVisitCounselsByVisitCounselIdsIn } from './visit-counsel.api';
import { orderApi } from '@/entities/order';
import { plannerApi } from '@/entities/planner';
import type { PlannerDayCounsel } from './usePlannerDayCounsels';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';

export interface UseRegionDayCounselsResult {
  counsels: PlannerDayCounsel[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

async function fetchRegionDayCounsels(
  serviceRegionId: string,
  date: string
): Promise<PlannerDayCounsel[]> {
  const slotsResponse = await plannerCounselSlotApi.getAllSlots(
    date,
    undefined,
    serviceRegionId,
    100
  );

  const visitCounselIds = new Set<string>();
  const plannerIdByVisitCounselId = new Map<string, string>();

  for (const slot of slotsResponse.slots ?? []) {
    if (slot.regionId !== serviceRegionId || slot.date !== date) continue;
    for (const s of slot.slots ?? []) {
      if (s.status === 'APPOINTED' && s.visitCounselId) {
        visitCounselIds.add(s.visitCounselId);
        plannerIdByVisitCounselId.set(s.visitCounselId, slot.plannerId);
      }
    }
  }

  const ids = Array.from(visitCounselIds);
  if (ids.length === 0) return [];

  const searchItems = await searchVisitCounselsByVisitCounselIdsIn(ids);

  const uniqueOrderIds = [...new Set(searchItems.map((c) => c.orderId))];
  const orderMap = new Map<
    string,
    { misoOrderId: string; location: DepartureLocationInfo | null }
  >();

  if (uniqueOrderIds.length > 0) {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderMap.set(order.id, {
        misoOrderId: order.misoOrderId,
        location: order.assignedDepartureLocation ?? null,
      });
    }
  }

  const plannerIdsUnique = Array.from(new Set(plannerIdByVisitCounselId.values()));
  const plannersResult = await Promise.all(
    plannerIdsUnique.map((id) => plannerApi.getPlannerById(id))
  );
  const plannerNameMap = new Map<string, string>();
  plannerIdsUnique.forEach((id, i) => {
    const p = plannersResult[i];
    if (p?.name) plannerNameMap.set(id, p.name);
  });

  const itemsFiltered = searchItems.filter((c) => c.serviceRegionId === serviceRegionId);
  const sorted = [...itemsFiltered].sort((a, b) =>
    a.scheduleTime < b.scheduleTime ? -1 : a.scheduleTime > b.scheduleTime ? 1 : 0
  );

  const byPlanner = new Map<string, typeof sorted>();
  for (const c of sorted) {
    const pid = plannerIdByVisitCounselId.get(c.id) ?? c.counselorId;
    if (!byPlanner.has(pid)) byPlanner.set(pid, []);
    byPlanner.get(pid)!.push(c);
  }

  const counsels: PlannerDayCounsel[] = [];
  for (const [, list] of byPlanner) {
    const sortedList = [...list].sort((a, b) =>
      a.scheduleTime < b.scheduleTime ? -1 : a.scheduleTime > b.scheduleTime ? 1 : 0
    );
    for (const c of sortedList) {
      const orderInfo = orderMap.get(c.orderId);
      counsels.push({
        visitCounselId: c.id,
        orderId: c.orderId,
        scheduleTime: c.scheduleTime,
        location: orderInfo?.location ?? null,
        type: 'other',
        counselorName: plannerNameMap.get(c.counselorId),
        counselorId: c.counselorId,
        supplyArea: c.supplyArea,
        misoOrderId: orderInfo?.misoOrderId,
      });
    }
  }

  counsels.sort((a, b) => {
    const pidA = a.counselorId ?? '';
    const pidB = b.counselorId ?? '';
    if (pidA !== pidB) return pidA.localeCompare(pidB);
    return (a.scheduleTime ?? '').localeCompare(b.scheduleTime ?? '');
  });

  return counsels;
}

/**
 * 해당 구역·일자에 배정된 모든 플래너의 상담 조회 (접수중 주문 상담지도용)
 */
export function useRegionDayCounsels(
  serviceRegionId: string | null | undefined,
  date: string | null | undefined,
  enabled = true
): UseRegionDayCounselsResult {
  const key =
    enabled && serviceRegionId && date ? ['region-day-counsels', serviceRegionId, date] : null;

  const { data, error, isLoading, mutate } = useSWR<PlannerDayCounsel[]>(
    key,
    key ? () => fetchRegionDayCounsels(serviceRegionId!, date!) : null
  );

  return {
    counsels: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error : new Error('상담 조회 실패')) : null,
    refetch: () => mutate(),
  };
}
